import { h } from 'preact';
import { useState } from 'preact/hooks';
import StepChooseGiftbox from '../multistep/steps/choose-giftbox';
import { remainingCapacity as calcRemainingCapacity, findBoxCapacity, fitsInBox } from '../utils/box-capacity';
import { removeGiftBox } from '../remove/remove-gift-box';
import CloseIcon from '../icons/close-icon';
import { useLoadGiftBox } from './use-load-gift-box';
import { saveGiftBoxEdits } from './save-gift-box-edits';
import { buildSelectableUnits } from './selectable-units';
import ItemSelection from './item-selection';
import { t } from '../../theme/locales';
import { GiftMessage } from '../multistep/gift-message';
/**
 * Edit Gift Box — single-page form rendered inside an offcanvas drawer.
 *
 * Reuses the same step components as the multistep creation flow but presents
 * them all at once (gift wrapping type, product selection, gift message) with
 * Cancel / Save Changes actions.
 */ const preserveItemsIfFit = (partial, currentState, selectableUnits, giftboxProducts)=>{
    if (partial.giftbox == null || partial.giftbox === currentState.giftbox || !Array.isArray(partial.items) || partial.items.length !== 0) {
        return null;
    }
    var _partial_variant, _findBoxCapacity;
    const newBoxCapacity = (_findBoxCapacity = findBoxCapacity(giftboxProducts, partial.giftbox, (_partial_variant = partial.variant) !== null && _partial_variant !== void 0 ? _partial_variant : '')) !== null && _findBoxCapacity !== void 0 ? _findBoxCapacity : undefined;
    const currentItems = Array.isArray(currentState.items) ? currentState.items : [];
    if (currentItems.length > 0 && fitsInBox(currentItems, selectableUnits, newBoxCapacity)) {
        return currentItems;
    }
    return null;
};
export const EditGiftBox = ({ itemKey , giftboxProducts =null , onClose  })=>{
    const [state, setStateRaw] = useState({
        giftbox: '',
        variant: '',
        items: [],
        giftboxMessage: '',
        complimentaryMessage: ''
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [giftBoxId, setGiftBoxId] = useState(null);
    const [cart, setCart] = useState(null);
    const selectableUnits = buildSelectableUnits((cart === null || cart === void 0 ? void 0 : cart.items) || [], giftBoxId);
    // Merge partial updates, normalising `items` to always be an array
    const setState = (partial)=>{
        const normalised = {
            ...partial
        };
        if (Object.prototype.hasOwnProperty.call(partial, 'items')) {
            const raw = partial.items;
            normalised.items = Array.isArray(raw) ? raw : raw ? [
                String(raw)
            ] : [];
        }
        // When changing the gift box (which resets items to []), preserve the
        // currently selected items if they still fit in the new box's capacity.
        const preserved = preserveItemsIfFit(normalised, state, selectableUnits, giftboxProducts);
        if (preserved !== null) {
            normalised.items = preserved;
        }
        setStateRaw((prev)=>({
                ...prev,
                ...normalised
            }));
    };
    useLoadGiftBox({
        itemKey,
        giftboxProducts,
        setStateRaw,
        setCart,
        setGiftBoxId,
        setLoading
    });
    const handleSave = async ()=>{
        setSaving(true);
        setError(null);
        try {
            await saveGiftBoxEdits({
                itemKey,
                state,
                giftboxProducts,
                onSuccess: onClose,
                onError: setError,
                onCartUpdate: setCart
            });
        } catch (err) {
            console.error('Error saving gift box edits', err);
            setError('Something went wrong. Please try again.');
            window.dispatchEvent(new CustomEvent('gift-wrapping:error', {
                detail: {
                    message: 'Error saving gift box edits',
                    error: err
                }
            }));
        } finally{
            setSaving(false);
        }
    };
    const selectedItems = Array.isArray(state.items) ? state.items : [];
    var _findBoxCapacity;
    // Box capacity — null means no box selected yet (unlimited)
    const boxCapacity = (_findBoxCapacity = findBoxCapacity(giftboxProducts, state.giftbox, state.variant)) !== null && _findBoxCapacity !== void 0 ? _findBoxCapacity : null;
    var _calcRemainingCapacity;
    const remainingCapacity = boxCapacity !== null ? (_calcRemainingCapacity = calcRemainingCapacity(selectedItems, selectableUnits, boxCapacity)) !== null && _calcRemainingCapacity !== void 0 ? _calcRemainingCapacity : null : null;
    const toggleItem = (unitKey)=>{
        const unit = selectableUnits.find((u)=>u.unitKey === unitKey);
        if (!unit) return;
        const isSelected = selectedItems.includes(unitKey);
        if (isSelected) {
            setState({
                items: selectedItems.filter((k)=>k !== unitKey)
            });
            return;
        }
        if (remainingCapacity !== null && unit.effectiveSpace > remainingCapacity) return;
        setState({
            items: [
                ...selectedItems,
                unitKey
            ]
        });
    };
    if (loading) {
        return h('div', {
            className: 'edit-gift-box edit-gift-box--loading'
        }, 'Loading…');
    }
    return h('div', {
        className: 'edit-gift-box edit-gift-box--cartDrawer'
    }, [
        // Header
        h('div', {
            className: 'edit-gift-box__content'
        }, [
            h('div', {
                className: 'edit-gift-box__header'
            }, [
                h('h2', {
                    className: 'edit-gift-box__title'
                }, t('gift_wrapping.edit.label')),
                h('p', {
                    className: 'edit-gift-box__subtitle'
                }, t('gift_wrapping.edit.description')),
                h('button', {
                    type: 'button',
                    className: 'edit-gift-box__close',
                    onClick: onClose,
                    'aria-label': 'Close'
                }, h(CloseIcon, null))
            ]),
            // Section: Gift Wrapping
            h('div', {
                className: 'edit-gift-box__section'
            }, [
                h('h3', {
                    className: 'edit-gift-box__section-title'
                }, t('gift_wrapping.steps.choose_giftbox.title')),
                h(StepChooseGiftbox, {
                    state,
                    setState,
                    giftboxProducts,
                    hideComplimentaryMessage: true
                })
            ]),
            h('hr', {
                className: 'edit-gift-box__divider'
            }),
            h('div', {
                className: 'edit-gift-box__section'
            }, [
                h('h3', {
                    className: 'edit-gift-box__section-title'
                }, t('gift_wrapping.steps.select_items.title')),
                h('p', {
                    className: 'edit-gift-box__section-description'
                }, t('gift_wrapping.steps.select_items.description')),
                h(ItemSelection, {
                    selectableUnits,
                    selectedItems,
                    remainingCapacity,
                    onToggle: toggleItem,
                    cartItems: (cart === null || cart === void 0 ? void 0 : cart.items) || []
                })
            ]),
            h('hr', {
                className: 'edit-gift-box__divider'
            }),
            h(GiftMessage, {
                className: 'edit-gift-box__section',
                title: t('gift_wrapping.steps.select_items.gift_message.label'),
                description: t('gift_wrapping.steps.select_items.gift_message.description'),
                placeholder: t('gift_wrapping.steps.select_items.gift_message.placeholder'),
                value: state.giftboxMessage,
                fieldName: 'giftbox_message',
                onChange: (v)=>setState({
                        giftboxMessage: v
                    })
            }),
            error ? h('div', {
                className: 'edit-gift-box__error'
            }, error) : null
        ]),
        h('div', {
            className: 'edit-gift-box__actions'
        }, [
            h('button', {
                type: 'button',
                className: 'o-button o-button--hollow edit-gift-box__cancel',
                onClick: onClose,
                disabled: saving
            }, t('gift_wrapping.steps.controls.cancel')),
            selectedItems.length === 0 ? h('button', {
                type: 'button',
                className: 'o-button o-button--danger edit-gift-box__save',
                onClick: async ()=>{
                    setSaving(true);
                    await removeGiftBox(itemKey);
                    setSaving(false);
                    onClose();
                },
                disabled: saving
            }, saving ? t('gift_wrapping.edit.removing') : t('gift_wrapping.edit.remove')) : h('button', {
                type: 'button',
                className: 'o-button edit-gift-box__save',
                onClick: async ()=>{
                    setSaving(true);
                    await handleSave();
                    setSaving(false);
                    onClose();
                },
                disabled: saving
            }, saving ? t('gift_wrapping.steps.controls.saving') : t('gift_wrapping.steps.controls.save'))
        ])
    ]);
};
